"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { toFarsiNumber } from "../../helper/helper";
import logo from "../../images/header-logo.png";

const HLSPlayer = ({
  src = "",
  subtitles = [],
  poster = "",
  title = "پلیر ویدیو HLS",
  autoplay = false,
  muted = false,
  controls = true,
  width = "100%",
  height = "auto",
  sampleUrls = [],
  onPlay = () => {},
  onPause = () => {},
  onTimeUpdate = () => {},
  onLoadStart = () => {},
  onLoadEnd = () => {},
  onError = () => {},
  onEnd = () => {},
}) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const containerRef = useRef(null);
  const progressRef = useRef(null);
  const volumeRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(muted ? 0 : 1);
  const [isMuted, setIsMuted] = useState(muted);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [hlsUrl, setHlsUrl] = useState(src);
  const [availableSubtitles, setAvailableSubtitles] = useState(subtitles);
  const [currentSubtitle, setCurrentSubtitle] = useState(-1);
  const [showSettings, setShowSettings] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [buffered, setBuffered] = useState(0);
  const [qualities, setQualities] = useState([]);
  const [currentQuality, setCurrentQuality] = useState("Auto");
  const [hasEnded, setHasEnded] = useState(false);

  const [customSubtitles, setCustomSubtitles] = useState([]);
  const [currentSubtitleText, setCurrentSubtitleText] = useState("");
  const [subtitleError, setSubtitleError] = useState("");

  // Default sample URLs if none provided
  const defaultSampleUrls = [
    {
      url: "https://demo.unified-streaming.com/k8s/features/stable/video/tears-of-steel/tears-of-steel.ism/.m3u8",
      title: "Tears of Steel",
    },
    {
      url: "https://bitdash-a.akamaihd.net/content/sintel/hls/playlist.m3u8",
      title: "Sintel",
    },
    {
      url: "https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8",
      title: "Test Stream",
    },
  ];

  const finalSampleUrls =
    sampleUrls.length > 0 ? sampleUrls : defaultSampleUrls;

  // Wrap loadHLS in useCallback
  const loadHLS = useCallback(
    async (url) => {
      if (!videoRef.current || !url.trim()) return;

      setIsLoading(true);

      try {
        if (videoRef.current) {
          videoRef.current.pause();
          setIsPlaying(false);
        }

        if (hlsRef.current) {
          try {
            hlsRef.current.stopLoad();
            hlsRef.current.detachMedia();
            hlsRef.current.destroy();
          } catch (error) {
            console.log("HLS cleanup error (expected):", error.message);
          }
          hlsRef.current = null;
        }

        if (videoRef.current) {
          videoRef.current.src = "";
          videoRef.current.load();
        }

        await new Promise((resolve) => setTimeout(resolve, 100));

        if (typeof window !== "undefined") {
          const Hls = (await import("hls.js")).default;

          if (Hls.isSupported()) {
            const hls = new Hls({
              enableWorker: true,
              lowLatencyMode: true,
              debug: false,
            });

            hlsRef.current = hls;

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
              console.log("HLS manifest loaded successfully");
              setIsLoading(false);

              // Get available qualities
              const levels = hls.levels.map((level, index) => ({
                index,
                height: level.height,
                width: level.width,
                bitrate: level.bitrate,
                label: `${level.height}p`,
              }));
              setQualities([{ index: -1, label: "Auto" }, ...levels]);

              if (autoplay) {
                videoRef.current.play();
              }
            });

            hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
              const level = hls.levels[data.level];
              if (level) {
                setCurrentQuality(`${level.height}p`);
              }
            });

            hls.on(Hls.Events.ERROR, (event, data) => {
              console.error("HLS error:", data);
              setIsLoading(false);

              if (data.fatal) {
                switch (data.type) {
                  case Hls.ErrorTypes.NETWORK_ERROR:
                    console.log(
                      "Network error occurred, attempting to reload..."
                    );
                    hls.startLoad(); // تلاش برای بارگذاری مجدد جریان
                    break;
                  case Hls.ErrorTypes.MEDIA_ERROR:
                    console.log(
                      "Media error occurred, attempting to recover..."
                    );
                    hls.recoverMediaError(); // تلاش برای بازیابی خطای رسانه
                    // اضافه کردن تلاش مجدد در صورت عدم موفقیت بازیابی
                    setTimeout(() => {
                      if (!videoRef.current.paused && videoRef.current.error) {
                        console.log("Media error recovery failed, retrying...");
                        loadHLS(hlsUrl); // بارگذاری مجدد جریان HLS
                      }
                    }, 2000);
                    break;
                  default:
                    console.log(
                      "Unrecoverable error, attempting to reload stream..."
                    );
                    // به جای تخریب، تلاش برای بارگذاری مجدد جریان
                    loadHLS(hlsUrl);
                    break;
                }
              } else {
                console.log("Non-fatal error, continuing playback...");
              }
            });

            hls.loadSource(url);
            hls.attachMedia(videoRef.current);
          } else if (
            videoRef.current.canPlayType("application/vnd.apple.mpegurl")
          ) {
            videoRef.current.src = url;
            videoRef.current.load();
            setIsLoading(false);
            if (autoplay) {
              videoRef.current.play();
            }
          }
        }
      } catch (error) {
        console.error("Error loading HLS:", error);
        setIsLoading(false);
      }
    },
    [autoplay]
  );

  // Update internal state when props change
  useEffect(() => {
    setHlsUrl(src);
    loadHLS(src);
  }, [src, loadHLS]);

  useEffect(() => {
    setAvailableSubtitles(subtitles);

    // Auto-select first subtitle if available and none is currently selected
    if (subtitles.length > 0 && currentSubtitle === -1) {
      setCurrentSubtitle(0);
      console.log("Auto-selected first subtitle:", subtitles[0]?.language);
    }
  }, [subtitles, currentSubtitle]);

  useEffect(() => {
    // Auto-select first subtitle when available
    if (availableSubtitles.length > 0 && currentSubtitle === -1) {
      setCurrentSubtitle(0);
      console.log(
        "Auto-enabled first subtitle:",
        availableSubtitles[0]?.language
      );
    }
  }, [availableSubtitles]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => {
      setCurrentTime(video.currentTime || 0);
      onTimeUpdate(video.currentTime || 0);
    };
    const updateDuration = () => setDuration(video.duration || 0);
    const handleLoadStart = () => {
      setIsLoading(true);
      onLoadStart();
    };
    const handleCanPlay = () => {
      setIsLoading(false);
      onLoadEnd();
    };
    const handleProgress = () => {
      if (video.buffered.length > 0) {
        setBuffered((video.buffered.end(0) / video.duration) * 100);
      }
    };
    const handleError = (e) => {
      console.error("Video error:", e);
      setIsLoading(false);
      onError(e);
    };
    const handlePlay = () => {
      setIsPlaying(true);
      onPlay();
    };
    const handlePause = () => {
      setIsPlaying(false);
      onPause();
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setHasEnded(true);
      onEnd();
    };

    video.addEventListener("timeupdate", updateTime);
    video.addEventListener("loadedmetadata", updateDuration);
    video.addEventListener("loadstart", handleLoadStart);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("progress", handleProgress);
    video.addEventListener("error", handleError);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("timeupdate", updateTime);
      video.removeEventListener("loadedmetadata", updateDuration);
      video.removeEventListener("loadstart", handleLoadStart);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("progress", handleProgress);
      video.removeEventListener("error", handleError);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
    };
  }, [onTimeUpdate, onLoadStart, onLoadEnd, onError, onPlay, onPause]);

  // Auto-hide controls - FIXED for mobile
  useEffect(() => {
    if (!controls) return;

    let timeout;
    let isMobile = false;

    // Detect if device is mobile
    const checkMobile = () => {
      return (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        ) ||
        "ontouchstart" in window ||
        window.innerWidth <= 768
      );
    };

    isMobile = checkMobile();

    const showControlsTemporarily = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(
        () => {
          if (isPlaying) setShowControls(false);
        },
        isMobile ? 4000 : 3000
      ); // Longer timeout for mobile
    };

    const hideControlsImmediately = () => {
      if (isPlaying && !isMobile) setShowControls(false); // Don't auto-hide on mobile when leaving
    };

    const container = containerRef.current;
    if (container) {
      if (isMobile) {
        // Mobile: Use touch events
        container.addEventListener("touchstart", showControlsTemporarily);
        container.addEventListener("touchmove", showControlsTemporarily);

        // Hide controls when video is playing and user stops interacting
        const handleTouchEnd = () => {
          clearTimeout(timeout);
          timeout = setTimeout(() => {
            if (isPlaying) setShowControls(false);
          }, 4000);
        };
        container.addEventListener("touchend", handleTouchEnd);
      } else {
        // Desktop: Use mouse events
        container.addEventListener("mousemove", showControlsTemporarily);
        container.addEventListener("mouseleave", hideControlsImmediately);
      }

      // Always show controls when video is paused
      if (!isPlaying) {
        setShowControls(true);
        clearTimeout(timeout);
      }
    }

    return () => {
      clearTimeout(timeout);
      if (container) {
        // Clean up all event listeners
        container.removeEventListener("mousemove", showControlsTemporarily);
        container.removeEventListener("mouseleave", hideControlsImmediately);
        container.removeEventListener("touchstart", showControlsTemporarily);
        container.removeEventListener("touchmove", showControlsTemporarily);
        container.removeEventListener("touchend", showControlsTemporarily);
      }
    };
  }, [isPlaying, controls]);

  // Load custom subtitles
  useEffect(() => {
    if (availableSubtitles.length > 0 && currentSubtitle >= 0) {
      const selectedSubtitle = availableSubtitles[currentSubtitle];
      if (selectedSubtitle && selectedSubtitle.src) {
        loadSubtitleFile(selectedSubtitle.src);
      }
    } else {
      setCustomSubtitles([]);
      setCurrentSubtitleText("");
    }
  }, [availableSubtitles, currentSubtitle]);

  // Update current subtitle text based on video time
  useEffect(() => {
    if (customSubtitles.length === 0 || currentSubtitle < 0) {
      setCurrentSubtitleText("");
      return;
    }

    const currentSub = customSubtitles.find(
      (sub) => currentTime >= sub.start && currentTime <= sub.end
    );

    setCurrentSubtitleText(currentSub ? currentSub.text : "");
  }, [customSubtitles, currentTime, currentSubtitle]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
  };

  const handleProgressClick = (e) => {
    if (!progressRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeClick = (e) => {
    if (!volumeRef.current) return;
    const rect = volumeRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newVolume = Math.max(0, Math.min(1, percent));
    videoRef.current.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    if (isMuted) {
      videoRef.current.volume = volume;
      setIsMuted(false);
    } else {
      videoRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  const skip = (seconds) => {
    if (!videoRef.current) return;
    const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Parse VTT subtitle file
  const parseVTT = (vttContent) => {
    const lines = vttContent.split("\n");
    const subtitles = [];
    let i = 0;

    // Skip WEBVTT header
    while (i < lines.length && !lines[i].includes("-->")) {
      i++;
    }

    while (i < lines.length) {
      const line = lines[i].trim();

      if (line.includes("-->")) {
        const [startTime, endTime] = line.split("-->").map((t) => t.trim());

        // Parse time to seconds
        const parseTime = (timeStr) => {
          const parts = timeStr.replace(",", ".").split(":");
          if (parts.length === 3) {
            return (
              Number.parseFloat(parts[0]) * 3600 +
              Number.parseFloat(parts[1]) * 60 +
              Number.parseFloat(parts[2])
            );
          }
          return 0;
        };

        const start = parseTime(startTime);
        const end = parseTime(endTime);

        // Get subtitle text (next non-empty lines)
        i++;
        let text = "";
        while (i < lines.length && lines[i].trim() !== "") {
          if (text) text += "\n";
          text += lines[i].trim();
          i++;
        }

        if (text) {
          subtitles.push({ start, end, text });
        }
      }
      i++;
    }

    return subtitles;
  };

  // Load subtitle file
  const loadSubtitleFile = async (subtitleUrl) => {
    try {
      setSubtitleError("");
      const response = await fetch(subtitleUrl, {
        mode: "cors",
        credentials: "omit",
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const vttContent = await response.text();
      const parsedSubtitles = parseVTT(vttContent);
      setCustomSubtitles(parsedSubtitles);
      console.log("Subtitles loaded:", parsedSubtitles.length, "entries");
      return parsedSubtitles;
    } catch (error) {
      console.error("Error loading subtitle:", error);
      setSubtitleError(`خطا در بارگذاری زیرنویس: ${error.message}`);
      return [];
    }
  };

  const handleSubtitleChange = (index) => {
    setCurrentSubtitle(index);
    setShowSettings(false);

    if (index >= 0) {
      console.log(`Selected subtitle: ${availableSubtitles[index]?.language}`);
    } else {
      console.log("Subtitles disabled");
      setCurrentSubtitleText("");
    }
  };

  const handlePlaybackRateChange = (rate) => {
    if (!videoRef.current) return;
    videoRef.current.playbackRate = rate;
    setPlaybackRate(rate);
    setShowSettings(false);
  };

  const handleQualityChange = (qualityIndex) => {
    if (!hlsRef.current) return;
    hlsRef.current.currentLevel = qualityIndex;
    setShowSettings(false);
  };

  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        try {
          hlsRef.current.stopLoad();
          hlsRef.current.detachMedia();
          hlsRef.current.destroy();
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
  }, []);

  return (
    <div
      className="w-full bg-black rounded-2xl overflow-hidden mt-2 relative"
      style={{ width, height }}
    >
      <img
        src={logo || "/placeholder.svg"}
        className="w-14 md:w-20 absolute left-4 bottom-4 z-40"
        alt=""
      />

      {/* Video Container */}
      <div
        ref={containerRef}
        className="relative aspect-video bg-black group cursor-pointer"
        onClick={togglePlay}
      >
        <video
          ref={videoRef}
          className="w-full h-full object-contain"
          poster={poster}
          muted={muted}
          crossOrigin="anonymous"
        />

        {/* Loading Spinner */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        )}

        {/* Custom Subtitle Overlay */}
        {currentSubtitleText && (
          <div className="absolute bottom-2 md:bottom-8 right-4 left-4 z-[99] pointer-events-none">
            <div className="bg-black/80 text-white text-center px-4 py-2 rounded-lg mx-auto max-w-fit">
              <div className="text-sm md:text-base lg:text-lg leading-relaxed">
                {currentSubtitleText}
              </div>
            </div>
          </div>
        )}

        {/* Subtitle Error Display */}
        {subtitleError && (
          <div className="absolute top-16 left-4 right-4 z-30">
            <div className="bg-red-600/90 text-white text-center px-4 py-2 rounded-lg mx-auto max-w-md">
              <div className="text-sm">{subtitleError}</div>
            </div>
          </div>
        )}

        {/* Controls Overlay */}
        {controls && (
          <div
            className={`absolute z-[999] inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 transition-opacity duration-300 ${
              showControls ? "opacity-100" : "opacity-0"
            }`}
          >
            {/* Top Controls */}
            <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-center">
              <div className="text-white font-semibold">{title}</div>
              <div className="flex gap-2">
                {/* Settings */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSettings(!showSettings);
                    }}
                    className="size-8 md:size-10 flex items-center justify-center text-white hover:bg-white/20 rounded transition-colors"
                  >
                    <svg
                      className="size-4 md:size-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </button>

                  {/* Settings Menu - FIXED: Changed from {true && to {showSettings && */}
                  {showSettings && (
                    <div className="absolute top-full left-0 mt-2 bg-black/90 rounded-lg p-3 min-w-48 z-[999999]">
                      {/* Quality Settings */}
                      {qualities.length > 1 && (
                        <>
                          <div className="text-white text-sm mb-2">کیفیت</div>
                          <div className="space-y-1 mb-3">
                            {qualities.map((quality) => (
                              <button
                                key={quality.index}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQualityChange(quality.index);
                                }}
                                className={`w-full text-xs px-2 py-1 rounded text-left transition-colors ${
                                  currentQuality === quality.label
                                    ? "bg-blue-600 text-white"
                                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                }`}
                              >
                                {toFarsiNumber(quality.label)}
                              </button>
                            ))}
                          </div>
                        </>
                      )}

                      {/* Playback Speed */}
                      <div className="text-white text-sm mb-2">سرعت پخش</div>
                      <div className="grid grid-cols-2 gap-1 mb-3">
                        {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
                          <button
                            key={rate}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePlaybackRateChange(rate);
                            }}
                            className={`text-xs px-2 py-1 rounded transition-colors ${
                              playbackRate === rate
                                ? "bg-blue-600 text-white"
                                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                            }`}
                          >
                            {toFarsiNumber(rate)}x
                          </button>
                        ))}
                      </div>

                      {/* Subtitles - FIXED VERSION */}
                      {availableSubtitles.length > 0 && (
                        <>
                          <div className="text-white text-sm mb-2">زیرنویس</div>
                          <div className="space-y-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSubtitleChange(-1);
                              }}
                              className={`w-full text-xs px-2 py-1 rounded text-left transition-colors ${
                                currentSubtitle === -1
                                  ? "bg-blue-600 text-white"
                                  : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                              }`}
                            >
                              خاموش
                            </button>

                            {availableSubtitles.map((subtitle, index) => (
                              <button
                                key={index}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSubtitleChange(index);
                                }}
                                className={`w-full text-xs px-2 py-1 rounded text-left transition-colors ${
                                  currentSubtitle === index
                                    ? "bg-blue-600 text-white"
                                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                }`}
                              >
                                {subtitle.language ||
                                  `زیرنویس ${toFarsiNumber(index + 1)}`}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Center Play Button */}
            {!isPlaying && !isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePlay();
                  }}
                  className="w-16 h-16 rounded-full bg-black/20 hover:bg-black/30 backdrop-blur-sm flex items-center justify-center transition-colors"
                >
                  <svg
                    className="w-8 h-8 text-white fill-white ml-1"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
              </div>
            )}

            {/* Bottom Controls */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
              {/* Progress Bar */}
              <div className="mb-4">
                <div
                  ref={progressRef}
                  onClick={handleProgressClick}
                  className="relative w-full h-1 bg-white/30 rounded-full cursor-pointer group"
                >
                  {/* Buffer Bar */}
                  <div
                    className="absolute top-0 left-0 h-full bg-white/40 rounded-full"
                    style={{ width: `${buffered}%` }}
                  />
                  {/* Progress Bar */}
                  <div
                    className="absolute top-0 left-0 h-full bg-yellow-700 rounded-full"
                    style={{
                      width: `${
                        duration ? (currentTime / duration) * 100 : 0
                      }%`,
                    }}
                  />
                  {/* Progress Handle */}
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-yellow-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{
                      left: `${duration ? (currentTime / duration) * 100 : 0}%`,
                      transform: "translateX(-50%) translateY(-50%)",
                    }}
                  />
                </div>
              </div>

              {/* Control Buttons */}
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-2">
                  {/* Fullscreen */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFullscreen();
                    }}
                    className="size-8 md:size-10 flex items-center justify-center hover:bg-white/20 rounded transition-colors"
                  >
                    {isFullscreen ? (
                      <svg
                        className="size-4 md:size-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5M15 9h4.5M15 9V4.5M15 9l5.5-5.5M9 15v4.5M9 15H4.5M9 15l-5.5 5.5M15 15h4.5M15 15v4.5m0-4.5l5.5 5.5"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="size-4 md:size-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                        />
                      </svg>
                    )}
                  </button>
                </div>

                <div className="flex flex-row-reverse items-center gap-1 md:gap-3">
                  {/* Play/Pause */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePlay();
                    }}
                    className="size-8 md:size-10 flex items-center justify-center hover:bg-white/20 rounded transition-colors"
                  >
                    {isPlaying ? (
                      <svg className="w-6 h-6 fill-white" viewBox="0 0 24 24">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 fill-white" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                  </button>

                  {/* Skip Backward */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      skip(-10);
                    }}
                    className="size-8 md:size-10 flex items-center justify-center hover:bg-white/20 rounded transition-colors relative"
                  >
                    <svg
                      className="size-4 md:size-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.333 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z"
                      />
                    </svg>
                    <span className="hidden md:block text-xs absolute -bottom-3">
                      {toFarsiNumber("10 ثانیه")}
                    </span>
                  </button>

                  {/* Skip Forward */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      skip(10);
                    }}
                    className="size-8 md:size-10 flex items-center justify-center hover:bg-white/20 rounded transition-colors relative"
                  >
                    <svg
                      className="size-4 md:size-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z"
                      />
                    </svg>
                    <span className="hidden md:block text-xs absolute -bottom-3">
                      {toFarsiNumber("10 ثانیه")}
                    </span>
                  </button>

                  {/* Volume */}
                  <div className="hidden md:flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMute();
                      }}
                      className="size-8 md:size-10 flex items-center justify-center hover:bg-white/20 rounded transition-colors"
                    >
                      {isMuted || volume === 0 ? (
                        <svg
                          className="size-4 md:size-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="size-4 md:size-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                          />
                        </svg>
                      )}
                    </button>
                    <div
                      ref={volumeRef}
                      onClick={handleVolumeClick}
                      className="w-20 h-1 bg-white/30 rounded-full cursor-pointer relative"
                    >
                      <div
                        className="absolute top-0 left-0 h-full bg-white rounded-full"
                        style={{ width: `${(isMuted ? 0 : volume) * 100}%` }}
                      />
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full"
                        style={{
                          left: `${(isMuted ? 0 : volume) * 100}%`,
                          transform: "translateX(-50%) translateY(-50%)",
                        }}
                      />
                    </div>
                  </div>

                  {/* Time Display */}
                  <div className="text-xs lg:text-sm font-mono">
                    {toFarsiNumber(formatTime(currentTime))} /{" "}
                    {toFarsiNumber(formatTime(duration))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HLSPlayer;
